import type { FC } from 'react';
import { useRef } from 'react';
import c from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import NoPreviewDumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo/NoPreviewDumbo';
import {
  getDeviationType,
  isMultiMediaDeviation,
  isTextualDeviation,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import UknownDumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo/Unknown';
import { useMainActionDeviationClick } from '../../../../hooks/useMainActionDeviationClick';
import { DEFAULT_THUMB_SIZE } from '../../../constants';
import StudioNoPreviewThumb from '../../../StudioThumb/lego/StudioNoPreviewThumb';
import CoverPlaceholder from '../CoverPlaceholder';

import s from './DeviationThumb.scss';

const TIER_THUMB_HEIGHT = 40;
const TIER_THUMB_WIDTH = 64;

export interface Props {
  deviation: PapiDeviation;
  width?: number;
  height?: number;
  noCropping?: boolean;
  noPadding?: boolean;
  largeStudioThumb?: boolean;
  isAppThumb?: boolean;
  useBackgroundColor?: boolean;
  withoutClickHandling?: boolean;
}

const DeviationThumb: FC<Props> = ({
  deviation,
  noCropping,
  noPadding,
  width: widthProp,
  height: heightProp,
  largeStudioThumb,
  isAppThumb,
  useBackgroundColor,
  /** turn off default click/mainAction handler (assume it's handled further up the dom) */
  withoutClickHandling,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { onClick } = useMainActionDeviationClick(deviation, containerRef, 'thumb');
  const deviationType = getDeviationType(deviation);
  const fileType = DeviationMediaService.getMediaFileType(deviation);
  const isTier = deviationType === DeviationTypes.TIER;
  const isStatus = deviationType === DeviationTypes.STATUS;

  const preview = DeviationMediaService.getDeviationFile(deviation, 'preview');
  const defaultWidth = isTier ? TIER_THUMB_WIDTH : DEFAULT_THUMB_SIZE;
  const defaultHeight = isTier ? TIER_THUMB_HEIGHT : DEFAULT_THUMB_SIZE;
  const width = widthProp || heightProp ? widthProp : defaultWidth;
  const height = widthProp || heightProp ? heightProp : defaultHeight;

  const cover_attachment = useFlag('cover_attachment');
  const isMultiVideoFlagEnabled = useFlag('multi_video');

  let thumb = (
    <Dumbo
      className={c(s['dumbo'], isStatus && s['status-thumb'])}
      deviation={deviation}
      width={width}
      height={height}
      noCropping={noCropping}
      useTextlessMode
      useBackgroundColor={useBackgroundColor}
    />
  );

  if (preview?.src.includes('minish/bears/empty')) {
    thumb = (
      <UknownDumbo className={s['dumbo']} width={width} height={height} fileType={fileType} />
    );
  }

  const likelyFileDeviation =
    !isTextualDeviation(deviation) && deviation.isDefaultImage && deviation.filetype !== 'png';
  if (isStatus || likelyFileDeviation) {
    const thumbType = deviationType === DeviationTypes.STATUS ? 'status' : fileType;

    if (largeStudioThumb) {
      if (isStatus) {
        // Thumb is already the dumbo
      } else {
        thumb = <StudioNoPreviewThumb deviation={deviation} width={width} height={height} />;
      }
    } else {
      thumb = (
        <NoPreviewDumbo
          className={s['dumbo']}
          deviation={deviation}
          width={width}
          height={height}
          fileType={thumbType}
        />
      );
    }
  }

  if (deviationType === DeviationTypes.JOURNAL) {
    const hasDeviationMedia = !!deviation.media?.types?.length;
    const coverImage = cover_attachment && hasDeviationMedia ? deviation : deviation.coverImage;
    thumb = coverImage ? (
      <Dumbo
        className={s['dumbo']}
        deviation={coverImage}
        width={width}
        height={height}
        useTextlessMode
      />
    ) : (
      <CoverPlaceholder width={width} height={height} />
    );
  }

  const isMulti = isMultiMediaDeviation(deviation, isMultiVideoFlagEnabled);
  const containedThumb = (
    <div
      className={c(
        s['thumb-container'],
        isMulti && s['thumb-container-multi'],
        noPadding && s['no-padding']
      )}
      ref={containerRef}
    >
      {thumb}
      {isMulti && (
        <div className={s['thumb-shadow']}>
          <div className={s['inner-shadow']} />
          <div className={s['outer-shadow']} />
        </div>
      )}
    </div>
  );

  return withoutClickHandling ? (
    containedThumb
  ) : (
    <PlainButtonOrLink onClick={onClick}>{containedThumb}</PlainButtonOrLink>
  );
};
DeviationThumb.displayName = 'DeviationThumb';

export default DeviationThumb;
